import { MutationSystem } from "@/lib/queuedMutations";
import type { Meeting, Team, Tenant, User } from "@repo/types";
import { createContext, useContext } from "react";
import type { Collapsible, EditorObject, EditorType } from "./utilities";
import { DeltaUndoStack } from "@repo/lib/undoStack";

export const useMeetingEditor = () => {
  const context = useContext(MeetingEditorContext);
  if (!context)
    throw new Error("Called useMeetingEditor from outside MeetingEditor");
  return context;
};

export interface UndoDelta {
  label: string;
  undo(): void;
  redo(): void;
}

export interface MeetingEditorContextType {
  viewOnly: boolean;
  mutationSystem: MutationSystem;
  tenant: Tenant;
  meeting: Meeting;
  team: Team;
  user: User;
  userMap: Record<string, User>;
  timeZone: string;
  openEditForm: <T extends EditorObject>(
    type: EditorType,
    object: T | null
  ) => Promise<T | null>;
  openLoadForm: <T extends EditorObject>(type: EditorType) => Promise<T | null>;
  collapseSet: Set<Collapsible>;
  updateAllCollapsed: () => void;
  undoStack: DeltaUndoStack<UndoDelta>;
  previewTemplate?: string;
  canViewConfidential: boolean;
  additionalDetails: boolean;
}

export const MeetingEditorContext =
  createContext<MeetingEditorContextType | null>(null);
